/**
 * 统一时间处理工具（基于 day.js）：
 * - 存储：统一使用「时间戳」存入后端（number ms）。
 * - 展示：统一按北京时间（UTC+8，Asia/Shanghai）格式化。
 *
 * 约定：
 * - 有合法时间字段（plannedEnd / actualStart / actualEnd …）归一化为毫秒时间戳（number）后进入计算 / 存储。
 * - 纯日期字符串 `YYYY-MM-DD` 视为「北京当天 00:00」的本地零点，避免时区偏移导致跨天错位。
 * - 「今天」取真实当前日期（北京时区），供耗时 / 超期等口径使用；需要确定性（如单测）
 *   时给下游纯函数显式传入 today，不要写死基准日。
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

/** 北京时区标识（统一展示口径） */
export const BEIJING_TZ = 'Asia/Shanghai';

/**
 * 「今天」（北京时区当天 `YYYY-MM-DD`，模块加载时取真实当前日期）。
 *
 * 单一事实源：`constants/app` 的 `TODAY` 由此再导出，业务「今天」口径（耗时 / 超期 / 排序）
 * 统一读它。历史这里是写死的仿真基准日，真实数据晚于基准日会让耗时被钳成 0、超期失真，
 * 故改为取真实时钟；需要确定性（如单测）时给下游纯函数显式传入 today，不要改这里。
 */
export const SIM_TODAY = dayjs().tz(BEIJING_TZ).format('YYYY-MM-DD');

/** 合法输入：时间戳(number) 或 日期字符串 'YYYY-MM-DD' / 'YYYY-MM-DD HH:mm' 或 Date / null / undefined / '' */
export type TimeInput = number | string | Date | null | undefined;

function isDayString(v: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(v);
}

/**
 * 归一化为北京时间毫秒时间戳。
 * - number：视为已是毫秒时间戳，原样返回。
 * - 纯日期字符串 'YYYY-MM-DD'：按北京时间当天 00:00 解析（避免时区偏移跨天）。
 * - 其他字符串 / Date：按北京时间解析。
 * - 非法 / 空 → null。
 */
export function normalizeToTs(value: TimeInput): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return value;
  let parsed: dayjs.Dayjs;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : null;
    }
    parsed = isDayString(trimmed)
      ? dayjs.tz(trimmed, BEIJING_TZ).startOf('day')
      : dayjs.tz(trimmed, BEIJING_TZ);
  } else {
    // Date 实例：取毫秒，再转到北京时区表达
    parsed = dayjs(value.getTime()).tz(BEIJING_TZ);
  }
  if (!parsed.isValid()) return null;
  return parsed.valueOf();
}

/** 当前是否为「今天」的日期字符串（'YYYY-MM-DD'，北京时区） */
export function todayStr(tz = BEIJING_TZ): string {
  return dayjs().tz(tz).format('YYYY-MM-DD');
}

/** 模拟基准日「今天」的北京零点时间戳（仿真用，确定性） */
export function simTodayStamp(tz = BEIJING_TZ): number {
  return dayjs.tz(SIM_TODAY, tz).startOf('day').valueOf();
}

/** 北京时间今天零点的真实时间戳（用于实际存储新写入） */
export function nowStamp(tz = BEIJING_TZ): number {
  return dayjs().tz(tz).valueOf();
}

/** 空时间占位符（表格 / 文案统一） */
export const EMPTY_TIME = '—';

/** 完整展示格式（各粒度由此截取） */
export const FMT_DATE = 'YYYY-MM-DD';
export const FMT_DATETIME_MIN = 'YYYY-MM-DD HH:mm';
export const FMT_DATETIME_SEC = 'YYYY-MM-DD HH:mm:ss';
export const FMT_TIME = 'HH:mm:ss';

/**
 * 展示粒度：在完整「日期 + 时分秒」口径上按需截取。
 * - date：仅日期
 * - datetime：日期 + 时:分
 * - datetimeSec：日期 + 时:分:秒（默认完整）
 * - time：仅时:分:秒
 */
export type TimeDisplayMode = 'date' | 'datetime' | 'datetimeSec' | 'time';

const DISPLAY_FMT: Record<TimeDisplayMode, string> = {
  date: FMT_DATE,
  datetime: FMT_DATETIME_MIN,
  datetimeSec: FMT_DATETIME_SEC,
  time: FMT_TIME,
};

/** 按展示粒度格式化；非法 / 空 → EMPTY_TIME */
export function formatDisplay(value: TimeInput, mode: TimeDisplayMode = 'datetimeSec', tz = BEIJING_TZ): string {
  const ts = normalizeToTs(value);
  if (ts === null) return EMPTY_TIME;
  return dayjs(ts).tz(tz).format(DISPLAY_FMT[mode]);
}

/** 按北京时间格式化 → 'YYYY-MM-DD' */
export function fmtDay(value: TimeInput, tz = BEIJING_TZ): string {
  return formatDisplay(value, 'date', tz);
}

/** 按北京时间格式化 → 'YYYY-MM-DD HH:mm' */
export function fmtDateTime(value: TimeInput, tz = BEIJING_TZ): string {
  return formatDisplay(value, 'datetime', tz);
}

/** 按北京时间格式化 → 'YYYY-MM-DD HH:mm:ss' */
export function fmtDateTimeSec(value: TimeInput, tz = BEIJING_TZ): string {
  return formatDisplay(value, 'datetimeSec', tz);
}

/** 按北京时间格式化 → 'HH:mm:ss' */
export function fmtTime(value: TimeInput, tz = BEIJING_TZ): string {
  return formatDisplay(value, 'time', tz);
}
